import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronDown, HelpCircle } from 'lucide-react'

interface FAQItem {
  q: string
  a: string
}

interface FAQCategory {
  title: string
  questions: FAQItem[]
}

export const BaresFAQSection: React.FC = () => {
  const { t } = useTranslation('bares')
  const [openIndexes, setOpenIndexes] = useState<Set<string>>(new Set())

  const categories = t('faq.categories', { returnObjects: true }) as Record<string, FAQCategory>

  const toggleQuestion = (categoryKey: string, questionIndex: number) => {
    const key = `${categoryKey}-${questionIndex}`
    setOpenIndexes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }

  const isOpen = (categoryKey: string, questionIndex: number) => {
    return openIndexes.has(`${categoryKey}-${questionIndex}`)
  }

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-amber-50/30 to-orange-50/30 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-20 w-96 h-96 bg-amber-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-orange-300/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
          >
            <HelpCircle className="w-4 h-4" />
            FAQ
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {t('faq.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('faq.subtitle')}
          </motion.p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {Object.entries(categories).map(([categoryKey, category], catIndex) => (
            <motion.div
              key={categoryKey}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: catIndex * 0.1 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                  {catIndex + 1}
                </div>
                {category.title}
              </h3>

              <div className="space-y-3">
                {category.questions.map((question, qIndex) => {
                  const isQuestionOpen = isOpen(categoryKey, qIndex)

                  return (
                    <div
                      key={qIndex}
                      className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden hover:border-amber-300 transition-colors"
                    >
                      <button
                        onClick={() => toggleQuestion(categoryKey, qIndex)}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-amber-50/50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 pr-4">{question.q}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-amber-600 flex-shrink-0 transition-transform duration-300 ${
                            isQuestionOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      <AnimatePresence initial={false}>
                        {isQuestionOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                          >
                            <div className="px-6 pb-6 pt-0 text-gray-700 leading-relaxed border-t border-gray-100">
                              {question.a}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
